import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { galleryFallbackImages } from './galleryFallbackData';

function endpoint(baseUrl, path) {
  return `${String(baseUrl).replace(/\/$/, '')}${path}`;
}

async function parseJson(response) {
  const text = await response.text();
  try {
    return text ? JSON.parse(text) : {};
  } catch {
    return { message: text || 'Unexpected server response.' };
  }
}

export const fetchGallery = createAsyncThunk(
  'gallery/fetchGallery',
  async ({ apiBaseUrl }, { rejectWithValue }) => {
    try {
      const response = await fetch(endpoint(apiBaseUrl, '/gallery'), {
        headers: { Accept: 'application/json' },
      });
      const payload = await parseJson(response);

      if (!response.ok || !payload.data) {
        throw new Error(payload.message || 'Unable to load gallery.');
      }

      return {
        apiBaseUrl,
        images: Array.isArray(payload.data) ? payload.data : [],
        isFallback: false,
      };
    } catch (error) {
      return {
        apiBaseUrl,
        images: galleryFallbackImages,
        isFallback: true,
      };
    }
  },
  {
    condition: ({ apiBaseUrl }, { getState }) => {
      const { gallery } = getState();
      return !(gallery.status === 'loading' || (gallery.fetched && gallery.apiBaseUrl === apiBaseUrl));
    },
  }
);

const initialState = {
  apiBaseUrl: null,
  items: [],
  fetched: false,
  status: 'idle',
  error: '',
  isFallback: false,
};

const gallerySlice = createSlice({
  name: 'gallery',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchGallery.pending, (state) => {
        state.status = 'loading';
        state.error = '';
      })
      .addCase(fetchGallery.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.error = '';
        state.fetched = true;
        state.apiBaseUrl = action.payload.apiBaseUrl;
        state.items = action.payload.images;
        state.isFallback = Boolean(action.payload.isFallback);
      })
      .addCase(fetchGallery.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload || action.error.message || 'Unable to load gallery.';
      });
  },
});

export default gallerySlice.reducer;
